import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { CalendarClock, Plus, X, Clock, AlertTriangle, Wrench, Calculator } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { mdy, mdyDow } from "@/lib/dateFmt";

const MOD_TYPES = [
  { value: "arrive_late", label: "Arrive late",   help: "You'll start later than your normal start time.  The manager needs to cover the front of the shift." },
  { value: "leave_early", label: "Leave early",   help: "You'll clock out before your normal end time.  The manager needs to cover the back of the shift." },
  { value: "long_lunch",  label: "Long lunch",    help: "You start and end on time, but take a longer break in the middle." },
];

// ---- Employee-side PTO calculator (inside the Request PTO tab) --------------
// Shows the accrual tier, takes a typed current balance, and walks the
// employee's existing pending/approved PTO plus the DRAFT dates the user is
// currently adding, so they can see per-request "hours left" tiles before
// submitting. Includes the "assumes no call-out / late / absent" caveat.
function EmployeePtoCheck({ ctx, balance, onBalance, draftDates, draftHours, draftWorkDays }) {
  const accrual = ctx?.accrual || { accrual_per_pp: 8, tier_label: "Default", hire_date: null };
  const rate = Number(accrual.accrual_per_pp || 8);
  const cur = parseFloat(balance);
  const hasBalance = !isNaN(cur);

  // Compose the timeline: existing commitments + the draft (as a synthetic tile).
  // We compute pp_crossings for the draft locally so the tile renders live as
  // dates are added/removed.
  const timeline = useMemo(() => {
    const rows = [...(ctx?.future_commitments || [])];
    if (draftDates && draftDates.length > 0) {
      const first = [...draftDates].sort()[0];
      const last  = [...draftDates].sort().slice(-1)[0];
      let ppCross = 0;
      try {
        const today = ctx?.today ? parseISO(ctx.today) : new Date();
        // BASE_ANCHOR_SUNDAY = 2026-01-04. We compute PP anchors from there.
        const BASE = new Date("2026-01-04T00:00:00");
        const daysFromBase = (d) => Math.floor((d - BASE) / 86400000);
        const anchorFor = (d) => {
          const diff = daysFromBase(d);
          const idx = Math.floor(diff / 14);
          const a = new Date(BASE); a.setDate(a.getDate() + idx * 14); return a;
        };
        const todayAnchor  = anchorFor(today);
        const firstAnchor  = anchorFor(parseISO(first));
        ppCross = Math.max(0, Math.floor((firstAnchor - todayAnchor) / (14 * 86400000)));
      } catch { ppCross = 0; }
      rows.push({
        request_id: "__draft__",
        status: "draft",
        first_date: first,
        last_date: last,
        dates: draftDates,
        pto_hours: Number(draftHours || 0),
        work_days: Number(draftWorkDays || 0),
        pp_crossings_before: ppCross,
        is_draft: true,
      });
      rows.sort((a, b) => (a.first_date || "").localeCompare(b.first_date || ""));
    }
    return rows;
  }, [ctx, draftDates, draftHours, draftWorkDays]);

  // Walk the balance forward tile-by-tile.
  let used = 0;
  const tiles = timeline.map((c) => {
    used += Number(c.pto_hours || 0);
    const asOfNow  = hasBalance ? cur - used : null;
    const withAccr = hasBalance ? cur - used + rate * (c.pp_crossings_before || 0) : null;
    return { ...c, asOfNow, withAccr };
  });
  const dateLabel = (iso) => {
    try { return mdyDow(iso); } catch { return iso; }
  };
  const fmtH = (h) => `${h >= 0 ? "" : "−"}${Math.abs(h).toFixed(2)}h`;

  return (
    <div
      className="rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white overflow-hidden"
      data-testid="emp-pto-check"
    >
      <div className="flex items-center gap-2 px-3 py-2 bg-indigo-600/5 border-b border-indigo-100">
        <span className="w-6 h-6 rounded-md bg-indigo-600 text-white flex items-center justify-center">
          <Calculator size={13} />
        </span>
        <span className="font-display font-semibold text-slate-800 text-sm">Do I have enough hours?</span>
        <span className="text-[11px] uppercase tracking-widest text-indigo-700 font-bold bg-indigo-100 border border-indigo-200 rounded px-1.5 py-0.5">
          {rate}h / pay period
        </span>
      </div>
      <div className="px-3 py-3 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-lg border border-slate-200 bg-white p-2.5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Your accrual tier</div>
            <div className="text-sm font-medium text-slate-800 mt-0.5">{accrual.tier_label}</div>
            <div className="text-[11px] text-slate-500 mt-1">
              {accrual.hire_date
                ? <>Hired {mdy(accrual.hire_date)} · {accrual.service_months} mo of service</>
                : <>No hire date on file — using 8 h/PP default. Ask your manager to add your hire date for exact math.</>}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-2.5">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold" htmlFor="emp-bal-input">
              Your current PTO balance (hours)
            </label>
            <Input
              id="emp-bal-input"
              data-testid="emp-pto-balance"
              type="number"
              min="0"
              step="0.25"
              value={balance}
              onChange={(e) => onBalance(e.target.value)}
              placeholder="Type it in — from your paystub"
              className="mt-1 tabular-nums"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              We don&apos;t store your balance — this is just a calculator.
            </div>
          </div>
        </div>

        {hasBalance && tiles.length === 0 && (
          <div className="text-xs text-slate-500 italic">
            Add a date above and we&apos;ll show what your balance looks like after.
          </div>
        )}

        {hasBalance && tiles.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">
              Balance after each of your upcoming PTO requests
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {tiles.map((t) => {
                const shortAsOf = t.asOfNow < 0;
                const shortWith = t.withAccr < 0;
                const rescued = shortAsOf && !shortWith;
                return (
                  <div
                    key={t.request_id}
                    data-testid={`emp-pto-tile-${t.request_id}`}
                    className={`rounded-lg border-2 p-2 ${
                      t.is_draft ? "border-fuchsia-400 ring-2 ring-fuchsia-200/60" : "border-slate-200"
                    } ${
                      shortWith ? "bg-rose-50" : rescued ? "bg-amber-50" : "bg-emerald-50/60"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[11px] font-semibold text-slate-800">
                        {dateLabel(t.first_date)}
                        {t.dates && t.dates.length > 1 && (
                          <span className="text-slate-500 font-normal"> → {dateLabel(t.last_date)}</span>
                        )}
                      </div>
                      <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
                        t.is_draft
                          ? "bg-fuchsia-100 border border-fuchsia-300 text-fuchsia-800"
                          : t.status === "approved"
                            ? "bg-emerald-100 border border-emerald-300 text-emerald-800"
                            : "bg-amber-100 border border-amber-300 text-amber-800"
                      }`}>
                        {t.is_draft ? "This request" : t.status}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-[11px] text-slate-600">
                      <span>Cost</span>
                      <span className="tabular-nums font-semibold text-rose-700">−{Number(t.pto_hours).toFixed(2)}h</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-slate-600">
                      <span>As it sits</span>
                      <span className={`tabular-nums font-bold ${shortAsOf ? "text-rose-700" : "text-slate-800"}`}>
                        {fmtH(t.asOfNow)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-slate-600">
                      <span>With accrual{t.pp_crossings_before > 0 ? ` (+${t.pp_crossings_before} PP)` : ""}</span>
                      <span className={`tabular-nums font-bold ${shortWith ? "text-rose-700" : "text-emerald-700"}`}>
                        {fmtH(t.withAccr)}
                      </span>
                    </div>
                    {shortWith && (
                      <div className="mt-1 text-[10px] font-bold text-rose-800 uppercase tracking-widest">
                        Not enough hours
                      </div>
                    )}
                    {rescued && (
                      <div className="mt-1 text-[10px] font-bold text-amber-800 uppercase tracking-widest">
                        Rescued by accrual
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-amber-800 mt-2 flex items-start gap-1 bg-amber-50 border border-amber-200 rounded-md p-1.5" data-testid="emp-pto-caveat">
              <AlertTriangle size={11} className="mt-[1px] shrink-0" />
              <span>
                * Projections assume you don&apos;t call out, arrive late, or get an absence mark between now and each date — anything that pulls PTO would lower these numbers.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PtoRequestDialog({ open, onClose, onSubmitted, facilityId }) {
  const [mode, setMode] = useState("pto"); // "pto" | "modify"
  // ---- PTO ----
  const [dates, setDates]     = useState([]);
  const [dateInput, setDateI] = useState("");
  const [reason, setReason]   = useState("");
  // ---- Employee-side PTO calculator ----
  const [ptoCtx, setPtoCtx]     = useState(null);   // { accrual, future_commitments, today }
  const [balance, setBalance]   = useState("");
  const [estimate, setEstimate] = useState({ pto_hours: 0, work_days: 0, per_date: [] });
  // ---- Modify ----
  const [modDate,  setModDate]  = useState("");
  const [modType,  setModType]  = useState("arrive_late");
  const [assignment, setAssignment] = useState(null);
  const [loadingA, setLoadingA] = useState(false);
  const [newStart, setNewStart] = useState("");
  const [newEnd,   setNewEnd]   = useState("");
  const [lunchStart, setLunchStart] = useState("12:00");
  const [lunchEnd,   setLunchEnd]   = useState("13:00");
  const [modReason, setModReason]   = useState("");
  const [busy, setBusy] = useState(false);

  // Reset every time the dialog closes
  useEffect(() => {
    if (!open) {
      setMode("pto");
      setDates([]); setDateI(""); setReason(""); setBalance("");
      setEstimate({ pto_hours: 0, work_days: 0, per_date: [] });
      setModDate(""); setModType("arrive_late"); setAssignment(null);
      setNewStart(""); setNewEnd(""); setLunchStart("12:00"); setLunchEnd("13:00"); setModReason("");
    }
  }, [open]);

  // Preload PTO context (accrual + upcoming commitments) once per open.
  useEffect(() => {
    if (!open || mode !== "pto") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/my-pto/context");
        if (!cancelled) setPtoCtx(data);
      } catch { if (!cancelled) setPtoCtx(null); }
    })();
    return () => { cancelled = true; };
  }, [open, mode]);

  // Re-estimate hours whenever the picked dates change (debounced by React batching).
  useEffect(() => {
    if (!open || mode !== "pto") return;
    if (dates.length === 0) { setEstimate({ pto_hours: 0, work_days: 0, per_date: [] }); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/my-pto/estimate", { dates });
        if (!cancelled) setEstimate(data);
      } catch { if (!cancelled) setEstimate({ pto_hours: 0, work_days: 0, per_date: [] }); }
    })();
    return () => { cancelled = true; };
  }, [open, mode, dates]);

  // Pull the employee's actual shift for the chosen date
  useEffect(() => {
    if (mode !== "modify" || !modDate || !facilityId) { setAssignment(null); return; }
    let cancelled = false;
    setLoadingA(true);
    (async () => {
      try {
        const { data } = await api.get(`/my/day/${modDate}?facility_id=${facilityId}`);
        if (!cancelled) {
          const a = data?.assignment;
          setAssignment(a);
          if (a) {
            setNewStart(a.start_time || "");
            setNewEnd(a.end_time || "");
          }
        }
      } catch { if (!cancelled) setAssignment(null); }
      if (!cancelled) setLoadingA(false);
    })();
    return () => { cancelled = true; };
  }, [mode, modDate, facilityId]);

  const addDate = () => {
    if (!dateInput) return;
    if (dates.includes(dateInput)) { setDateI(""); return; }
    setDates([...dates, dateInput].sort()); setDateI("");
  };
  const removeDate = (d) => setDates(dates.filter((x) => x !== d));

  const submitPto = async () => {
    if (dates.length === 0) return toast.error("Add at least one date");
    setBusy(true);
    try {
      await api.post("/pto-requests", { dates, reason });
      toast.success("PTO request submitted");
      onSubmitted && onSubmitted();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to submit");
    }
    setBusy(false);
  };

  const submitMod = async () => {
    if (!assignment) return toast.error("Pick a date you have a shift on");
    const body = { facility_id: facilityId, date: modDate, type: modType, reason: modReason || null };
    if (modType === "arrive_late") body.new_start = newStart;
    if (modType === "leave_early") body.new_end   = newEnd;
    if (modType === "long_lunch") { body.lunch_start = lunchStart; body.lunch_end = lunchEnd; }
    setBusy(true);
    try {
      await api.post("/shift-modification-requests", body);
      toast.success("Modification request sent to your manager");
      onSubmitted && onSubmitted();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to submit");
    }
    setBusy(false);
  };

  const canSubmitMod = assignment && (
    (modType === "arrive_late" && newStart && newStart !== assignment.start_time) ||
    (modType === "leave_early" && newEnd   && newEnd   !== assignment.end_time)   ||
    (modType === "long_lunch"  && lunchStart && lunchEnd)
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="pto-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <CalendarClock size={18} className="text-fuchsia-600" /> Time off &amp; shift changes
          </DialogTitle>
          <DialogDescription>
            Request full days off, or ask to change your hours for one day.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode} className="mt-1">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="pto"    data-testid="pto-mode-pto">
              <CalendarClock size={14} className="mr-1" /> Request PTO
            </TabsTrigger>
            <TabsTrigger value="modify" data-testid="pto-mode-modify">
              <Wrench size={14} className="mr-1" /> Modify hours
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {mode === "pto" ? (
          <div className="space-y-3">
            <div>
              <Label>Add a date</Label>
              <div className="flex gap-2 mt-1.5">
                <Input type="date" value={dateInput} onChange={(e) => setDateI(e.target.value)} data-testid="pto-date-input" />
                <Button variant="outline" onClick={addDate} data-testid="pto-date-add">
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {dates.map((d) => (
                  <span key={d} className="inline-flex items-center gap-1 rounded-md border border-fuchsia-300 bg-fuchsia-100 text-fuchsia-900 px-2 py-0.5 text-xs">
                    {mdyDow(d)}
                    <button onClick={() => removeDate(d)} data-testid={`pto-date-remove-${d}`}
                      className="hover:text-red-600" aria-label="Remove">
                      <X size={10} />
                    </button>
                  </span>
                ))}
                {dates.length === 0 && <span className="text-xs text-slate-500">No dates added yet.</span>}
              </div>
            </div>

            <EmployeePtoCheck
              ctx={ptoCtx}
              balance={balance}
              onBalance={setBalance}
              draftDates={dates}
              draftHours={estimate.pto_hours}
              draftWorkDays={estimate.work_days}
            />

            <div>
              <Label>Reason (optional)</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Family trip, appointment, etc." data-testid="pto-reason" />
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="mod-form">
            <div>
              <Label>Date</Label>
              <Input type="date" value={modDate} onChange={(e) => setModDate(e.target.value)} data-testid="mod-date" />
              {modDate && loadingA && <div className="text-xs text-slate-500 mt-1">Loading your shift…</div>}
              {modDate && !loadingA && !assignment && (
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[12px] text-amber-900 flex items-start gap-1.5">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  You don&apos;t have a shift on that day.
                </div>
              )}
              {assignment && (
                <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700 flex flex-wrap gap-3 items-center" data-testid="mod-assignment-info">
                  <span className="inline-flex items-center rounded-md border border-blue-300 bg-blue-100 text-blue-800 px-1.5 py-0.5 text-[11px] font-bold">
                    {assignment.shift_code}
                  </span>
                  <span>{assignment.shift_label}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={11} />{assignment.start_time} – {assignment.end_time}</span>
                  <span className="tabular-nums">{assignment.hours}h</span>
                </div>
              )}
            </div>

            {assignment && (
              <>
                <div>
                  <Label>Change type</Label>
                  <Select value={modType} onValueChange={setModType}>
                    <SelectTrigger data-testid="mod-type" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MOD_TYPES.map(t => (<SelectItem key={t.value} value={t.value} data-testid={`mod-type-${t.value}`}>{t.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {MOD_TYPES.find(t => t.value === modType)?.help}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className={modType !== "arrive_late" ? "text-slate-400" : ""}>Start</Label>
                    <Input type="time"
                      value={modType === "arrive_late" ? newStart : assignment.start_time}
                      onChange={(e) => setNewStart(e.target.value)}
                      disabled={modType !== "arrive_late"}
                      data-testid="mod-start"
                    />
                  </div>
                  <div>
                    <Label className={modType !== "leave_early" ? "text-slate-400" : ""}>End</Label>
                    <Input type="time"
                      value={modType === "leave_early" ? newEnd : assignment.end_time}
                      onChange={(e) => setNewEnd(e.target.value)}
                      disabled={modType !== "leave_early"}
                      data-testid="mod-end"
                    />
                  </div>
                </div>

                {modType === "long_lunch" && (
                  <div className="grid grid-cols-2 gap-3" data-testid="mod-lunch-fields">
                    <div>
                      <Label>Lunch start</Label>
                      <Input type="time" value={lunchStart} onChange={(e) => setLunchStart(e.target.value)} data-testid="mod-lunch-start" />
                    </div>
                    <div>
                      <Label>Lunch end</Label>
                      <Input type="time" value={lunchEnd} onChange={(e) => setLunchEnd(e.target.value)} data-testid="mod-lunch-end" />
                    </div>
                  </div>
                )}

                <div>
                  <Label>Reason (optional)</Label>
                  <Textarea value={modReason} onChange={(e) => setModReason(e.target.value)}
                    placeholder="Doctor appt, kid pickup, etc." data-testid="mod-reason" />
                </div>

                <div className="text-[11px] text-slate-500 flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200 p-2">
                  <AlertTriangle size={12} className="mt-0.5 text-amber-700 shrink-0" />
                  Once your manager approves, the missing hours will show up as an open coverage slot for that day.
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {mode === "pto" ? (
            <Button className="bg-fuchsia-600 hover:bg-fuchsia-700" onClick={submitPto} disabled={busy || dates.length === 0} data-testid="pto-submit">
              Submit request
            </Button>
          ) : (
            <Button className="bg-fuchsia-600 hover:bg-fuchsia-700" onClick={submitMod} disabled={busy || !canSubmitMod} data-testid="mod-submit">
              Submit modification
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
